import * as React from "react";
import type { ComponentType, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  CalendarClock,
  ClipboardList,
  CreditCard,
  LayoutDashboard,
  User,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn, Logo, Tooltip } from "@chotca/ui";
import { useLogout, useMe, useNotificationStream } from "@chotca/api-client";
import { useAuthStore } from "@chotca/auth";
import { AppShellViewportLock } from "@chotca/app-shell";
import { setAccessToken } from "@/boot/access-token";
import { AvatarMenu } from "./employer/avatar-menu";
import { EditProfileDialog } from "./employer/edit-profile-dialog";
import { ChangePasswordDialog } from "./employer/change-password-dialog";
import { NotificationsPopover } from "./employer/notifications-popover";
import { CreditModalsProvider } from "./employer/credit-modals";
import {
  SubscriptionModalsProvider,
  useSubscriptionModals,
} from "./employer/subscription-modals";

type NavDef = {
  href: string;
  labelKey: string;
  // Shorter label for the cramped mobile bottom bar. Falls back to labelKey.
  mobileLabelKey?: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number }>;
};

type NavItem = NavDef & { mobileOnly?: boolean };

const NAV: NavItem[] = [
  { href: "/dashboard", labelKey: "dashboard", icon: LayoutDashboard },
  { href: "/applicants", labelKey: "applicants", icon: Users },
  { href: "/postings", labelKey: "postings", icon: ClipboardList },
  { href: "/interviews", labelKey: "interviews", icon: CalendarClock },
  {
    href: "/billing",
    labelKey: "billing",
    mobileLabelKey: "billingShort",
    icon: CreditCard,
  },
  { href: "/profile", labelKey: "profile", icon: User },
];

function navActive(pathname: string, href: string) {
  if (href === "/dashboard") {
    return pathname === "/dashboard" || pathname === "/dashboard/";
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

function RailNavLink({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: NavDef["icon"];
}) {
  const { pathname } = useLocation();
  const active = navActive(pathname, href);
  return (
    <Tooltip label={label} side="right">
      <Link
        to={href}
        aria-current={active ? "page" : undefined}
        aria-label={label}
        className={cn(
          "relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-200",
          active
            ? "bg-lime text-ink shadow-sm ring-2 ring-lime/35"
            : "text-ash hover:bg-mist hover:text-ink active:scale-95",
        )}
      >
        <Icon size={22} strokeWidth={1.5} />
      </Link>
    </Tooltip>
  );
}

function MobileNavTab({
  href,
  label,
  icon: Icon,
  active,
}: {
  href: string;
  label: string;
  icon: NavDef["icon"];
  active: boolean;
}) {
  return (
    <Link
      to={href}
      aria-current={active ? "page" : undefined}
      aria-label={label}
      className={cn(
        "relative z-10 flex h-12 flex-1 flex-col items-center justify-center gap-0.5 rounded-full transition-colors duration-200",
        active ? "text-ink" : "text-ash hover:text-ink",
      )}
    >
      <Icon size={20} strokeWidth={active ? 2.1 : 1.7} />
      <span
        className={cn(
          "max-w-full truncate px-1 text-[10.5px] leading-none transition-[font-weight,color] duration-200",
          active ? "font-bold" : "font-semibold",
        )}
      >
        {label}
      </span>
    </Link>
  );
}

export function EmployerAppShell({ children }: { children: ReactNode }) {
  return (
    <CreditModalsProvider>
      <SubscriptionModalsProvider>
        <EmployerAppShellInner>{children}</EmployerAppShellInner>
      </SubscriptionModalsProvider>
    </CreditModalsProvider>
  );
}

function ShellAvatarMenu({ align }: { align: "right" | "bottom" }) {
  const me = useMe();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const { openUpgrade } = useSubscriptionModals();
  const nav = useNavigate();
  const logout = useLogout({
    redirectTo: "/login",
    onCleared: () => setAccessToken(null),
  });
  const [editOpen, setEditOpen] = React.useState(false);
  const [pwOpen, setPwOpen] = React.useState(false);

  if (!me.data) return null;
  const user = me.data;
  const orgRole =
    (user.memberships.find((m) => m.orgId === activeOrgId)?.role as
      | "OWNER"
      | "HR"
      | undefined) ??
    (user.memberships[0]?.role as "OWNER" | "HR" | undefined) ??
    null;

  return (
    <>
      <AvatarMenu
        user={user}
        orgRole={orgRole}
        align={align}
        onEditProfile={() => setEditOpen(true)}
        onChangePassword={() => setPwOpen(true)}
        onUpgrade={openUpgrade}
        onOrgSettings={() => nav("/org")}
        onLogout={() => {
          void logout();
        }}
      />
      <EditProfileDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        user={user}
      />
      <ChangePasswordDialog
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        email={user.email}
      />
    </>
  );
}

function EmployerAppShellInner({ children }: { children: ReactNode }) {
  const tNav = useTranslations("appNav");
  const tCommon = useTranslations("common");
  const { pathname } = useLocation();
  const homeActive = navActive(pathname, "/dashboard");
  const onDashboard = homeActive;
  // "Tìm ứng viên" is a focused full-screen deck (docs/01-product.md) — hide the mobile
  // bottom nav so it doesn't sit above the deck / its modals.
  const hideBottomNav = pathname.startsWith("/candidates");

  // One realtime notification stream for the whole authenticated shell — the
  // bell badge updates instantly, with the 30s poll as fallback.
  const me = useMe();
  useNotificationStream(!!me.data);

  return (
    <div className="relative h-svh overflow-hidden bg-mist md:h-auto md:min-h-screen md:overflow-visible">
      <AppShellViewportLock />
      <aside className="fixed left-4 top-1/2 z-40 hidden max-h-[min(620px,calc(100vh-2rem))] -translate-y-1/2 flex-col md:flex">
        <div className="flex flex-col items-center gap-3 rounded-[9999px] border border-smoke bg-paper px-2 py-4 shadow-[0_12px_48px_rgba(15,15,15,0.1)]">
          <Link
            to="/dashboard"
            aria-label={tCommon("appName")}
            className={cn(
              "mb-1 flex h-11 w-11 items-center justify-center rounded-full transition-transform hover:scale-105 active:scale-95",
              homeActive && "ring-2 ring-smoke",
            )}
          >
            <Logo variant="square" size={36} />
          </Link>
          <div
            className="flex flex-col gap-2"
            role="navigation"
            aria-label="Main"
          >
            {NAV.filter((n) => !n.mobileOnly).map((n) => (
              <RailNavLink
                key={n.href}
                href={n.href}
                label={tNav(n.labelKey)}
                icon={n.icon}
              />
            ))}
          </div>
          <div className="mt-2 flex w-full flex-col items-center gap-2 border-t border-smoke pt-3">
            <NotificationsPopover align="right" variant="rail" />
            <ShellAvatarMenu align="right" />
          </div>
        </div>
      </aside>

      <div
        data-employer-shell-main
        className="flex h-full flex-col overflow-y-auto overscroll-contain pb-22 [-webkit-overflow-scrolling:touch] md:h-auto md:min-h-screen md:overflow-visible md:overscroll-auto md:pb-0 md:pl-[calc(1rem+4rem+1.75rem)]"
      >
        {onDashboard ? (
          <div className="pointer-events-none sticky top-0 z-30 flex justify-end px-4 pt-[max(0.75rem,env(safe-area-inset-top))] md:hidden">
            <div className="pointer-events-auto">
              <NotificationsPopover align="bottom" />
            </div>
          </div>
        ) : null}

        <main
          className={cn(
            "flex-1 px-4 pb-6 md:px-10 md:py-10",
            onDashboard
              ? "-mt-2 pt-0 md:mt-0"
              : "pt-[max(1rem,env(safe-area-inset-top))]",
          )}
        >
          <div className="mx-auto w-full max-w-5xl space-y-6">{children}</div>
        </main>
      </div>

      {hideBottomNav ? null : <MobileBottomNav pathname={pathname} />}
    </div>
  );
}

function MobileBottomNav({ pathname }: { pathname: string }) {
  const tNav = useTranslations("appNav");
  const activeIndex = Math.max(
    0,
    NAV.findIndex((n) => navActive(pathname, n.href)),
  );
  return (
    <div
      data-employer-mobile-nav-wrap
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 md:hidden"
    >
      <nav
        aria-label="Main"
        className="pointer-events-auto relative mx-4 flex items-stretch rounded-full border border-smoke/70 bg-paper/95 p-1.5 shadow-[0_8px_28px_rgba(15,15,15,0.12)] backdrop-blur-md"
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-1.5 z-0"
        >
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-lime shadow-[0_4px_14px_rgba(200,255,91,0.35)] transition-transform duration-[400ms] ease-[cubic-bezier(0.32,0.72,0,1)]"
            style={{
              width: `${100 / NAV.length}%`,
              transform: `translateX(${activeIndex * 100}%)`,
            }}
          />
        </span>
        {NAV.map((n, i) => (
          <MobileNavTab
            key={n.href}
            href={n.href}
            label={tNav(n.mobileLabelKey ?? n.labelKey)}
            icon={n.icon}
            active={i === activeIndex}
          />
        ))}
      </nav>
    </div>
  );
}
